/**
 * 部屋の一覧（issue #103・docs/01 15-1・7-9）。
 *
 * この画面でできること:
 *  - 使っている部屋を探して、その部屋の画面を開く
 *  - 削除した部屋を「元に戻す」
 *  - 削除した部屋を「完全に消す」（「削除したトークを完全に消す」ができる役職のみ・**2段構え**）
 *
 * 決めごと（docs/06 決定22）:
 *  - 表示は「使っている部屋」「削除した部屋」の2つに分け、**押せるボタンで**切り替える
 *  - 「完全に消す」は、**消えるものの件数と部屋の名前を見せてから**実行する（原則4）
 *  - 終わったら必ず結果を出す（原則1・2）
 *
 * 部屋の作成・名前や設定の変更・参加者の出し入れ・削除は、開いた先の画面で行います
 * （docs/06 原則3「1画面1目的」）。
 *
 * **1対1はこの一覧に出ません**（管理者は1対1の名前や設定に触れないため・決定27・issue #242）。
 * 1対1に「事故対応」の指定を付けるときは、別の画面（/rooms/direct）で方を選んで探します。
 * 一覧は1回に100件ずつ読み、続きがあるときは「さらに表示する」を出します（黙って切らない）。
 * 削除した部屋の一覧も同じです（issue #278）。
 *
 * 使っている部屋は、LINE のトーク一覧と同じ「**最後にメッセージがあった順**」に並べ、名前で探せます
 * （issue #293）。見ている間にメッセージがあった部屋も、「さらに表示する」を最後まで押せば一覧の下に
 * 出ます（サーバが一覧を開いた時点を続きの位置に入れて読む・issue #284）。同じ部屋は二度並べません。
 * 削除した部屋は「削除した日時の新しい順」です。
 *
 * 元に戻す・完全に消すのあとは、**いま出ている件数ぶんを読み直して差し替える**ので、
 * 「さらに表示する」で読み足した分と表示の位置は消えません（issue #424）。
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError, messageForStatus } from '../api/client.js';
import { useApi } from '../api/context.js';
import { usePagedList, type PagedChunk } from '../api/paged-list.js';
import type {
  ArchivedChannel,
  ArchivedChannelList,
  ChannelChoices,
  ManagedChannel,
  ManagedChannelList,
  PurgePreview,
  PurgeProgress,
  PurgeSummary,
} from '../api/types.js';
import { useSession } from '../auth/session-context.js';
import { channelSiteLabel, channelTypeLabel, retentionLabel } from '../channels/labels.js';
import { LoadMoreFooter } from '../components/LoadMoreFooter.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { Button } from '../components/ui/Button.js';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.js';
import { DataTable } from '../components/ui/DataTable.js';
import { FormCard } from '../components/ui/FormCard.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { ResultBanner } from '../components/ui/ResultBanner.js';
import { can } from '../menu/visible.js';
import { withReason } from '../format/sentence.js';
import { formatTableDateTime } from '../format/datetime.js';
import { nextStepAfter } from '../api/load-failure.js';
import { ROLE_REQUIREMENTS, useRoleWording, type RoleWording } from '../auth/role-wording.js';

/** 一覧の切り替え（使っている部屋／削除した部屋）。 */
type Tab = 'active' | 'deleted';

/** 絞り込みの条件（入力しただけでは効かない。押したときだけ一覧を読み直す）。 */
interface Filters {
  /** 名前に含まれる言葉（issue #293） */
  q: string;
  type: string;
  siteId: string;
}

const EMPTY_FILTERS: Filters = { q: '', type: '', siteId: '' };

/** 画面に出す結果のお知らせ。 */
interface Banner {
  tone: 'success' | 'error';
  message: string;
  nextStep?: ReactNode;
  /**
   * 裏で消し続けているトークの「消しはじめました」のお知らせ（#405 のレビュー）。
   * 消し終わったら、このお知らせを「消しました」に差し替える（「しばらくかかります」を残さない）
   */
  purging?: { channelId: string; doneMessage: string };
}

/** 件数を「60,000件」の形にする（一覧の進み具合と同じ区切り・#405 のレビュー）。 */
function count(n: number): string {
  return `${n.toLocaleString('ja-JP')}件`;
}

export function ChannelsPage(): ReactNode {
  const api = useApi();
  const { profile } = useSession();
  const grant = profile?.grant;
  // 案内に出す役職の呼び名（その会社の呼び名・issue #480）
  const wording = useRoleWording();
  const [search] = useSearchParams();

  const [tab, setTab] = useState<Tab>('active');
  const [draft, setDraft] = useState<Filters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<Filters>(EMPTY_FILTERS);
  const [choices, setChoices] = useState<ChannelChoices | null>(null);
  const [choicesError, setChoicesError] = useState<string | null>(null);
  /** 「この条件で探す」を押すたびに1つ進む（同じ条件でも最初から読み直す） */
  const [searchCount, setSearchCount] = useState(0);
  /** 元に戻す・完全に消すのあと、削除した部屋の一覧を読み直すための番号 */
  const [deletedVersion, setDeletedVersion] = useState(0);

  /** いま確認の窓に出している「完全に消す」の中身（1回目の答え）。 */
  const [purgePreview, setPurgePreview] = useState<PurgePreview | null>(null);
  const [busyChannelId, setBusyChannelId] = useState<string | null>(null);
  const [result, setResult] = useState<Banner | null>(null);

  const canPurge = grant ? can(grant, 'channel.purge') : false;
  const canManage = grant ? can(grant, 'channel.manage_any') : false;
  // 1対1に「事故対応」の指定を付けられるのは、全社を受け持つ方だけ（決定27）
  const canMarkDirect = grant ? can(grant, 'retention.change') && grant.scope === 'org' : false;

  const reloadDeleted = useCallback(() => setDeletedVersion((n) => n + 1), []);
  /** 裏で消していたトークを消し終えた。一覧を読み直し、「消しはじめました」のお知らせを差し替える */
  const purgeFinished = useCallback(
    (channelId: string) => {
      reloadDeleted();
      setResult((current) =>
        current?.purging?.channelId === channelId
          ? { tone: 'success', message: current.purging.doneMessage }
          : current,
      );
    },
    [reloadDeleted],
  );

  // 種類と拠点の選択肢は、画面を開いたときに一度だけ読む
  useEffect(() => {
    let cancelled = false;
    api
      .get<ChannelChoices>('/admin/channels/choices')
      .then((loaded) => {
        if (cancelled) return;
        setChoices(loaded);
        setChoicesError(null);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        // 選択肢が読めなくても一覧は出す。ただし**黙って空にはしない**
        setChoices({ types: [], sites: [] });
        setChoicesError(caught instanceof ApiError ? caught.message : messageForStatus(0));
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  /** 削除した部屋を元に戻す（何度でもやり直せる操作なので、確認の窓は出さない）。 */
  async function restore(id: string, name: string): Promise<void> {
    setBusyChannelId(id);
    setResult(null);
    try {
      await api.post(`/channels/${id}/restore`);
      setResult({ tone: 'success', message: `「${name}」を元に戻しました。` });
      reloadDeleted();
    } catch (caught: unknown) {
      setResult({
        tone: 'error',
        message: withReason(`「${name}」を元に戻せませんでした`, toMessage(caught)),
        nextStep: nextStepAfter(caught, '少し時間をおいて、もう一度お試しください。'),
      });
    } finally {
      setBusyChannelId(null);
    }
  }

  /**
   * 「完全に消す」の1段目。**ここではまだ何も消えません。**
   * 何が消えるか（部屋の名前・投稿と添付の件数）をサーバから受け取り、確認の窓に出します。
   */
  async function askPurge(id: string): Promise<void> {
    setBusyChannelId(id);
    setResult(null);
    try {
      const preview = await api.post<PurgePreview>(`/channels/${id}/purge`, { body: {} });
      setPurgePreview(preview);
    } catch (caught: unknown) {
      setResult({
        tone: 'error',
        message: withReason(`完全に消す準備ができませんでした`, toMessage(caught)),
        nextStep: purgeNextStep(
          caught,
          id,
          nextStepAfter(caught, '少し時間をおいて、もう一度お試しください。'),
          wording,
        ),
      });
    } finally {
      setBusyChannelId(null);
    }
  }

  /** 「完全に消す」の2段目。1段目で受け取った合図を添えて、実際に消します。 */
  async function confirmPurge(): Promise<void> {
    const preview = purgePreview;
    if (!preview) return;
    setBusyChannelId(preview.channel_id);
    try {
      const summary = await api.post<PurgeSummary>(`/channels/${preview.channel_id}/purge`, {
        body: { confirmation_token: preview.confirmation_token },
      });
      setPurgePreview(null);
      const counts = `（メッセージ${count(summary.message_count)}・写真などのファイル${count(summary.attachment_count)}）`;
      const doneMessage = `「${summary.channel_name}」を完全に消しました${counts}。`;
      setResult(
        summary.status === 'in_progress'
          ? {
              // 大きなトークは裏で消し続ける（issue #396・決定22）。待たせずに、進み具合を一覧に出す
              tone: 'success',
              message:
                `「${summary.channel_name}」を完全に消しはじめました${counts}。` +
                'メッセージが多いため、消し終わるまでしばらくかかります。',
              nextStep:
                '進み具合は下の一覧に出ます。この画面を閉じても、消し続けます（元に戻すことはできません）。',
              purging: { channelId: preview.channel_id, doneMessage },
            }
          : { tone: 'success', message: doneMessage },
      );
      reloadDeleted();
    } catch (caught: unknown) {
      setPurgePreview(null);
      setResult({
        tone: 'error',
        message: withReason(
          `「${preview.channel_name}」を完全に消せませんでした`,
          toMessage(caught),
        ),
        nextStep: purgeNextStep(
          caught,
          preview.channel_id,
          '確認の画面を開き直して、もう一度お試しください。',
          wording,
        ),
      });
    } finally {
      setBusyChannelId(null);
    }
  }

  return (
    <article>
      <PageHeader
        title="トーク"
        description="トークを探して開きます。名前や設定の変更・参加者の追加と削除・保存期間・「事故対応」の指定・削除は、開いた先で行います。"
        actions={
          canManage || canMarkDirect ? (
            <div className="admin-row-actions">
              {canMarkDirect ? (
                <Link className="ui-button ui-button-secondary" to="/rooms/direct">
                  1対1に「事故対応」の指定を付ける
                </Link>
              ) : null}
              {canManage ? (
                <Link className="ui-button ui-button-secondary" to="/rooms/new">
                  トークを作る
                </Link>
              ) : null}
            </div>
          ) : undefined
        }
      />

      {/* 部屋の画面で削除したあと、この一覧へ戻ってきたとき（黙って戻さない・docs/06 原則1） */}
      {search.get('deleted') === '1' && result === null ? (
        <ResultBanner
          tone="success"
          message="トークを削除しました。"
          nextStep="一覧から消え、メッセージを送れなくなりました。中身は保存期間まで残ります。元に戻すときは「削除したトーク」からどうぞ。"
        />
      ) : null}

      {result ? (
        <ResultBanner
          tone={result.tone}
          message={result.message}
          {...(result.nextStep !== undefined ? { nextStep: result.nextStep } : {})}
        />
      ) : null}

      {/* 一覧の切り替えはタブの見た目にする。主役の赤いボタンと同じ見た目だと、
          赤いボタンが1画面に3つ並び、どれが大事な操作か分からなくなるため（総点検 M14） */}
      <div className="admin-tabs" role="group" aria-label="一覧の切り替え">
        <button
          type="button"
          className="admin-tab"
          aria-pressed={tab === 'active'}
          onClick={() => {
            setTab('active');
            setResult(null);
          }}
        >
          使っているトーク
        </button>
        <button
          type="button"
          className="admin-tab"
          aria-pressed={tab === 'deleted'}
          onClick={() => {
            setTab('deleted');
            setResult(null);
          }}
        >
          削除したトーク
        </button>
      </div>

      {tab === 'active' ? (
        <FormCard
          title="探す"
          submitLabel="この条件で探す"
          submitBusyLabel="探しています…"
          onSubmit={() => {
            setApplied(draft);
            setSearchCount((n) => n + 1);
          }}
          onCancel={() => {
            setDraft(EMPTY_FILTERS);
            setApplied(EMPTY_FILTERS);
            setSearchCount((n) => n + 1);
          }}
          cancelLabel="条件をすべて外す"
        >
          {choicesError ? (
            <ResultBanner
              tone="info"
              message={withReason(`種類と拠点の選択肢を読み込めませんでした`, choicesError)}
              nextStep="一覧はそのままご覧いただけます。絞り込みたいときは、画面を開き直してください。"
            />
          ) : null}
          <div className="admin-filters">
            <label className="ui-field" htmlFor="channels-q">
              <span>名前</span>
              <input
                id="channels-q"
                type="search"
                value={draft.q}
                maxLength={100}
                onChange={(event) => setDraft({ ...draft, q: event.target.value })}
              />
            </label>
            <label className="ui-field" htmlFor="channels-type">
              <span>種類</span>
              <select
                id="channels-type"
                value={draft.type}
                onChange={(event) => setDraft({ ...draft, type: event.target.value })}
              >
                <option value="">すべての種類</option>
                {choices?.types.map((type) => (
                  <option key={type.key} value={type.key}>
                    {channelTypeLabel(type.key)}
                  </option>
                ))}
              </select>
            </label>
            <label className="ui-field" htmlFor="channels-site">
              <span>拠点</span>
              <select
                id="channels-site"
                value={draft.siteId}
                onChange={(event) => setDraft({ ...draft, siteId: event.target.value })}
              >
                <option value="">すべての拠点</option>
                {choices?.sites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </FormCard>
      ) : null}

      {tab === 'active' ? (
        <ActiveList filters={applied} searchCount={searchCount} />
      ) : (
        <DeletedList
          version={deletedVersion}
          canPurge={canPurge}
          busyChannelId={busyChannelId}
          onRestore={restore}
          onAskPurge={askPurge}
          onPurgeFinished={purgeFinished}
        />
      )}

      <ConfirmDialog
        open={purgePreview !== null}
        title="このトークを完全に消します"
        message={
          purgePreview ? (
            <>
              「{purgePreview.channel_name}」のメッセージ{count(purgePreview.message_count)}
              {purgePreview.attachment_count > 0
                ? `と、写真・動画・ファイル${count(purgePreview.attachment_count)}`
                : ''}
              を消します。
            </>
          ) : (
            ''
          )
        }
        reversibility="元に戻せません。消したことは操作履歴に残ります。"
        confirmLabel="完全に消す"
        busy={busyChannelId !== null && purgePreview !== null}
        onConfirm={() => void confirmPurge()}
        onCancel={() => setPurgePreview(null)}
      />
    </article>
  );
}

/** 使っている部屋の一覧（1回に100件ずつ・続きは「さらに表示する」）。 */
function ActiveList({
  filters,
  searchCount,
}: {
  filters: Filters;
  searchCount: number;
}): ReactNode {
  const api = useApi();
  const list = usePagedList<ManagedChannel>(
    // 絞り込みを変えたら最初から読み直す（前の条件の続きは足さない・issue #284）
    `active:${filters.q}:${filters.type}:${filters.siteId}:${searchCount}`,
    async (cursor): Promise<PagedChunk<ManagedChannel>> => {
      const loaded = await api.get<ManagedChannelList>('/admin/channels', {
        query: { ...filterQuery(filters), ...(cursor !== null ? { cursor } : {}) },
      });
      return { items: loaded.channels, next: loaded.next_cursor ?? null };
    },
    (channel) => channel.id,
  );

  if (list.error !== null) return <ErrorBlock failure={list.error} onRetry={list.reload} />;
  if (list.items === null) return <LoadingBlock label="トークを読み込んでいます…" />;
  if (list.items.length === 0) {
    return (
      <EmptyBlock label="この条件に当てはまるトークはありませんでした。条件を外してお試しください。" />
    );
  }
  return (
    <>
      <p className="admin-note">
        最後にメッセージがあった順に並んでいます（一覧を開いたときの順です。開いたあとにメッセージがあったトークは、いちばん下に足されます）。
      </p>
      <DataTable label="使っているトークの一覧">
        <thead>
          <tr>
            <th scope="col">名前</th>
            <th scope="col">種類</th>
            <th scope="col">拠点</th>
            <th scope="col">参加人数</th>
            <th scope="col">保存期間</th>
            <th scope="col">事故対応</th>
            <th scope="col">作った日時</th>
            <th scope="col">
              最後の
              <wbr />
              メッセージ
            </th>
            <th scope="col">
              <span className="admin-visually-hidden">開く</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {list.items.map((channel) => (
            <tr key={channel.id}>
              <th scope="row" className="ui-cell-wrap">
                {channel.name}
                {channel.dormant_at ? (
                  // 休眠のトーク（issue #96）。参加している方の一覧には出ない。解くのは休眠の棚卸しの画面
                  <>
                    {' '}
                    <span className="admin-badge">休眠中</span>
                  </>
                ) : null}
              </th>
              <td>{channelTypeLabel(channel.type)}</td>
              <td className="ui-cell-soft">{channelSiteLabel(channel.site_name)}</td>
              <td>{channel.member_count}名</td>
              <td>{retentionLabel(channel.retention_days)}</td>
              <td>
                {channel.is_evidence ? (
                  <span className="admin-badge">指定あり</span>
                ) : (
                  <span className="admin-mark-no">なし</span>
                )}
              </td>
              <td className="ui-cell-date">{formatTableDateTime(channel.created_at)}</td>
              <td className="ui-cell-date">{formatTableDateTime(channel.last_message_at)}</td>
              <td>
                <Link
                  className="ui-button ui-button-secondary ui-button-compact"
                  to={`/rooms/${channel.id}`}
                >
                  開く
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>
      <LoadMoreFooter list={list} />
    </>
  );
}

/** 削除した部屋の一覧（元に戻す・完全に消す・1回に100件ずつ・続きは「さらに表示する」）。 */
function DeletedList({
  version,
  canPurge,
  busyChannelId,
  onRestore,
  onAskPurge,
  onPurgeFinished,
}: {
  /** 元に戻す・完全に消すのあとに1つ進む（いま出ている件数ぶんを読み直す） */
  version: number;
  canPurge: boolean;
  busyChannelId: string | null;
  onRestore: (id: string, name: string) => Promise<void>;
  onAskPurge: (id: string) => Promise<void>;
  /** 裏で消していたトークを消し終えた（一覧を読み直す） */
  onPurgeFinished: (channelId: string) => void;
}): ReactNode {
  const api = useApi();
  const list = usePagedList<ArchivedChannel>(
    'deleted',
    async (cursor): Promise<PagedChunk<ArchivedChannel>> => {
      const loaded = await api.get<ArchivedChannelList>('/channels/deleted', {
        query: cursor !== null ? { cursor } : {},
      });
      return { items: loaded.channels, next: loaded.next_cursor ?? null };
    },
    (channel) => channel.id,
  );

  // 元に戻す・完全に消すのあと（version が進んだとき）は、いま出ている件数ぶんを読み直して差し替える。
  // 最初から読み直すと、「さらに表示する」で読み足した分と表示の位置が消えるため（issue #424）
  const refreshRef = useRef(list.refresh);
  useEffect(() => {
    refreshRef.current = list.refresh;
  });
  const shownVersion = useRef(version);
  useEffect(() => {
    if (shownVersion.current === version) return;
    shownVersion.current = version;
    void refreshRef.current();
  }, [version]);

  if (list.error !== null) return <ErrorBlock failure={list.error} onRetry={list.reload} />;
  if (list.items === null) return <LoadingBlock label="削除したトークを読み込んでいます…" />;
  if (list.items.length === 0) {
    return <EmptyBlock label="削除したトークはありません。" />;
  }
  return (
    <>
      <p className="admin-note">
        削除したトークは一覧から消え、メッセージも送れなくなりますが、中身は保存期間まで残っています。元に戻すと、続きとしてまたお使いいただけます。削除した日時の新しい順に並んでいます。
      </p>
      <DataTable label="削除したトークの一覧">
        <thead>
          <tr>
            <th scope="col">名前</th>
            <th scope="col">種類</th>
            <th scope="col">削除した日時</th>
            <th scope="col">残っているメッセージ</th>
            <th scope="col">
              <span className="admin-visually-hidden">操作</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {list.items.map((channel) => (
            <tr key={channel.id}>
              <th scope="row" className="ui-cell-wrap">
                {channel.name}
              </th>
              <td>{channelTypeLabel(channel.type)}</td>
              <td className="ui-cell-date">{formatTableDateTime(channel.archived_at)}</td>
              <td>{count(channel.message_count)}</td>
              <td>
                {channel.purge_progress ? (
                  // 裏で消している途中（issue #396）。元に戻すことも、もう一度押すこともできない
                  <PurgeProgressNote
                    channelId={channel.id}
                    initial={channel.purge_progress}
                    onFinished={() => onPurgeFinished(channel.id)}
                  />
                ) : (
                  <div className="admin-row-actions">
                    <Button
                      variant="secondary"
                      compact
                      busy={busyChannelId === channel.id}
                      onClick={() => void onRestore(channel.id, channel.name)}
                    >
                      元に戻す
                    </Button>
                    {!canPurge ? null : channel.is_evidence === true ? (
                      // 「事故対応」の指定がある間は完全に消せない（決定32・#334 L-5）。
                      // 押してから断られるのではなく、先にそれと分かるようにする
                      <small className="ui-cell-wrap">
                        「事故対応」の指定があるため、消せません。
                        <br />
                        <Link to={`/rooms/${channel.id}`}>指定を外す</Link>
                      </small>
                    ) : (
                      <Button
                        variant="danger"
                        compact
                        busy={busyChannelId === channel.id}
                        onClick={() => void onAskPurge(channel.id)}
                      >
                        完全に消す
                      </Button>
                    )}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>
      <LoadMoreFooter list={list} />
    </>
  );
}

/** 進み具合を読み直す間隔（ミリ秒） */
const PURGE_PROGRESS_POLL_MS = 3_000;

/**
 * 「完全に消す」を裏で進めているトークの進み具合（issue #396・決定22）。
 * 数秒ごとに読み直し、消し終えたら一覧を読み直す（そのトークは一覧から消える）。
 */
function PurgeProgressNote({
  channelId,
  initial,
  onFinished,
}: {
  channelId: string;
  initial: { done: number; total: number };
  onFinished: () => void;
}): ReactNode {
  const api = useApi();
  const [progress, setProgress] = useState(initial);
  const finishedRef = useRef(onFinished);
  useEffect(() => {
    finishedRef.current = onFinished;
  });
  useEffect(() => {
    let cancelled = false;
    const timer = window.setInterval(() => {
      api
        .get<PurgeProgress>(`/channels/${channelId}/purge`)
        .then((loaded) => {
          if (cancelled) return;
          if (loaded.status === 'done') {
            window.clearInterval(timer);
            finishedRef.current();
            return;
          }
          setProgress({ done: loaded.done, total: loaded.total });
        })
        .catch(() => {
          // 読めなかった回は前の数のまま（次の回にもう一度読む）
        });
    }, PURGE_PROGRESS_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [api, channelId]);
  return (
    <small className="ui-cell-wrap" role="status">
      完全に消しています（{progress.total.toLocaleString('ja-JP')}件中
      {progress.done.toLocaleString('ja-JP')}件）。
      <br />
      この画面を閉じても消し続けます。
    </small>
  );
}

/** 絞り込みの条件を、一覧の窓口へ渡す形にする。 */
function filterQuery(filters: Filters): Record<string, string> {
  const q = filters.q.trim();
  return {
    ...(q !== '' ? { q } : {}),
    ...(filters.type !== '' ? { type: filters.type } : {}),
    ...(filters.siteId !== '' ? { site_id: filters.siteId } : {}),
  };
}

/**
 * 「完全に消す」を断られたときの次の一手。
 * 「事故対応」の指定があるトークは完全に消せない（決定32・サーバが `evidence_hold` で断る）。
 * 待っても直らないので、指定を外す場所をお伝えする。
 */
function purgeNextStep(
  caught: unknown,
  channelId: string,
  fallback: ReactNode,
  wording: RoleWording,
): ReactNode {
  if (caught instanceof ApiError && caught.code === 'evidence_hold') {
    return (
      <>
        消す必要があるときは、先に
        <Link to={`/rooms/${channelId}`}>このトークの画面</Link>
        {/* 役職の呼び名はその会社のもの（issue #480） */}
        で「事故対応」の指定を外してから、もう一度「完全に消す」を押してください（
        {wording.who(ROLE_REQUIREMENTS.orgRetention)}だけが外せます）。
      </>
    );
  }
  return fallback;
}

/** 失敗の理由を、運用される方が読める言葉にする。 */
function toMessage(caught: unknown): string {
  return caught instanceof ApiError ? caught.message : messageForStatus(0);
}
